const { useState, useEffect } = React;

const ROLES = [
  { value: 'tenant_admin', label: 'Tenant admin' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'agent', label: 'Agent' },
  { value: 'viewer', label: 'Viewer' }
];

function apiError(err, fallback) {
  var data = err && err.data;
  if (data && data.message) return String(data.message);
  if (data && data.detail) return String(data.detail);
  if (err && err.message) return String(err.message);
  return fallback;
}

function userPath(scope, tenantId, userId, suffix) {
  if (scope === 'platform') {
    var base = '/api/admin/tenants/' + tenantId + '/users';
    if (userId == null) return base;
    return base + '/' + userId + (suffix || '');
  }
  if (userId == null) return '/api/users';
  return '/api/users/' + userId + (suffix || '');
}

export function userMustChangePassword(user) {
  if (!user) return false;
  return user.must_change_password === true || user.must_change_password === 1 || user.must_change_password === '1';
}

export function UserEditModal({ userRow, tenantId, scope, api, toast, actorId, onClose, onSaved }) {
  var row = userRow || {};
  var [name, setName] = useState(row.name || '');
  var [email, setEmail] = useState(row.email || '');
  var [phone, setPhone] = useState(row.phone_e164 || '');
  var [role, setRole] = useState(row.role || 'agent');
  var [active, setActive] = useState(row.is_active !== false);
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState('');
  var [temporaryPassword, setTemporaryPassword] = useState('');

  useEffect(function () {
    setName(row.name || '');
    setEmail(row.email || '');
    setPhone(row.phone_e164 || '');
    setRole(row.role && ROLES.some(function (item) { return item.value === row.role; }) ? row.role : 'agent');
    setActive(row.is_active !== false);
    setError('');
    setTemporaryPassword('');
  }, [row.id]);

  var isSelf = actorId != null && Number(actorId) === Number(row.id);

  function save(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    api('PATCH', userPath(scope, tenantId, row.id), {
      name: name,
      email: email,
      phone_e164: phone.trim() ? phone.trim() : null,
      role: role,
      is_active: !!active
    }).then(function () {
      if (toast) toast('User updated.', 'success');
      if (onSaved) onSaved();
    }).catch(function (err) {
      var msg = apiError(err, 'Could not update that user.');
      setError(msg);
      if (toast) toast(msg, 'error');
    }).finally(function () { setSaving(false); });
  }

  function resetPassword() {
    setSaving(true);
    setError('');
    api('POST', userPath(scope, tenantId, row.id, '/reset-password'), {}).then(function (res) {
      if (res && res.mode === 'email_sent') {
        setTemporaryPassword('');
        if (toast) toast('Password reset email sent.', 'success');
      } else {
        setTemporaryPassword(res && res.temporaryPassword ? res.temporaryPassword : '');
        if (toast) toast('Temporary password created. Copy it now.', 'success');
      }
    }).catch(function (err) {
      var msg = apiError(err, 'Could not reset the password.');
      setError(msg);
      if (toast) toast(msg, 'error');
    }).finally(function () { setSaving(false); });
  }

  function signOutEverywhere() {
    if (typeof window !== 'undefined' && !window.confirm('Sign this user out of every session?')) return;
    setSaving(true);
    setError('');
    api('POST', userPath(scope, tenantId, row.id, '/sign-out-everywhere'), {}).then(function () {
      if (toast) toast('Signed out of every session.', 'success');
    }).catch(function (err) {
      var msg = apiError(err, 'Could not sign that user out.');
      setError(msg);
      if (toast) toast(msg, 'error');
    }).finally(function () { setSaving(false); });
  }

  return (
    <div className="acct-modal-backdrop" onClick={onClose}>
      <div className="acct-modal" onClick={function (e) { e.stopPropagation(); }}>
        <h3 className="acct-title">Edit user</h3>
        <form className="acct-form" onSubmit={save}>
          {error ? <div className="acct-error">{error}</div> : null}
          <label className="acct-label">Name
            <input className="acct-input" value={name} onChange={function (e) { setName(e.target.value); }} />
          </label>
          <label className="acct-label">Email
            <input className="acct-input" type="email" value={email} onChange={function (e) { setEmail(e.target.value); }} required />
          </label>
          <label className="acct-label">Phone (E.164)
            <input className="acct-input" type="tel" value={phone} onChange={function (e) { setPhone(e.target.value); }} placeholder="+14155551234" />
          </label>
          <label className="acct-label">Role
            <select className="acct-select" value={role} onChange={function (e) { setRole(e.target.value); }}>
              {ROLES.map(function (item) { return <option key={item.value} value={item.value}>{item.label}</option>; })}
            </select>
          </label>
          <label className="acct-label">
            <span className="acct-row">
              <input type="checkbox" checked={!!active} disabled={isSelf} onChange={function (e) { setActive(e.target.checked); }} />
              Active
            </span>
            {isSelf ? <span className="acct-muted">You cannot turn off your own account.</span> : null}
          </label>
          {temporaryPassword ? (
            <div className="acct-secret">
              <div className="acct-ok">Temporary password — shown once. They must change it at next sign-in.</div>
              <input className="acct-input" readOnly value={temporaryPassword} aria-label="Temporary password" />
            </div>
          ) : null}
          <div className="acct-row">
            <button className="acct-button" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button className="acct-button-ghost" type="button" disabled={saving} onClick={resetPassword}>Reset password</button>
            <button className="acct-button-ghost" type="button" disabled={saving} onClick={signOutEverywhere}>Sign out everywhere</button>
            <button className="acct-button-ghost" type="button" onClick={onClose}>Close</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function TenantUsersPanel({ tenantId, api, toast, scope, actorId }) {
  var [users, setUsers] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [editing, setEditing] = useState(null);

  function load() {
    if (scope === 'platform' && !tenantId) {
      setLoading(false);
      setUsers([]);
      return;
    }
    setLoading(true);
    setError('');
    api('GET', userPath(scope, tenantId)).then(function (res) {
      setUsers((res && res.users) || []);
    }).catch(function (err) {
      setError(apiError(err, 'Could not load users.'));
      setUsers([]);
    }).finally(function () { setLoading(false); });
  }

  useEffect(function () { load(); }, [tenantId, scope]);

  return (
    <div className="acct-panel">
      <h3 className="acct-title">Users</h3>
      <p className="acct-help">Edit name, email, phone, and role. Reset password gives a temporary password they must change at next sign-in.</p>
      {error ? <div className="acct-error">{error}</div> : null}
      {loading ? <div className="acct-muted">Loading users…</div> : null}
      {!loading && users.length === 0 ? <div className="acct-muted">No users in this workspace.</div> : null}
      {users.length ? (
        <div className="acct-table-wrap">
          <table className="acct-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map(function (user) {
                return (
                  <tr key={user.id}>
                    <td>{user.name || '—'}</td>
                    <td>{user.email || '—'}</td>
                    <td>{user.phone_e164 || '—'}</td>
                    <td>{user.role || '—'}</td>
                    <td>{user.is_active ? 'Yes' : 'No'}</td>
                    <td>
                      <button type="button" className="acct-button-ghost" onClick={function () { setEditing(user); }}>Edit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {editing ? (
        <UserEditModal
          userRow={editing}
          tenantId={tenantId}
          scope={scope}
          api={api}
          toast={toast}
          actorId={actorId}
          onClose={function () { setEditing(null); }}
          onSaved={function () { setEditing(null); load(); }}
        />
      ) : null}
    </div>
  );
}

export function MyAccountPanel({ user, api, toast }) {
  var [email, setEmail] = useState((user && user.email) || '');
  var [emailPassword, setEmailPassword] = useState('');
  var [phone, setPhone] = useState((user && (user.phone_e164 || user.phone_number)) || '');
  var [phoneCode, setPhoneCode] = useState('');
  var [phoneSent, setPhoneSent] = useState(false);
  var [currentPassword, setCurrentPassword] = useState('');
  var [nextPassword, setNextPassword] = useState('');
  var [busy, setBusy] = useState(false);
  var [error, setError] = useState('');

  useEffect(function () {
    if (!api) return;
    api('GET', '/api/auth/me').then(function (res) {
      var row = res && res.user;
      if (!row) return;
      setEmail(row.email || '');
      setPhone(row.phone_e164 || row.phone_number || '');
    }).catch(function () {});
  }, [user && user.id]);

  function changeEmail(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    api('POST', '/api/auth/account/email', { email: email, current_password: emailPassword }).then(function () {
      setEmailPassword('');
      if (toast) toast('Email updated.', 'success');
    }).catch(function (err) {
      var msg = apiError(err, 'Could not change the email.');
      setError(msg);
      if (toast) toast(msg, 'error');
    }).finally(function () { setBusy(false); });
  }

  function sendPhoneCode(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    api('POST', '/api/auth/account/phone/start', { phone_e164: phone }).then(function (res) {
      setPhoneSent(true);
      if (toast) toast(res && res.phone_hint ? 'Code sent to ' + res.phone_hint + '.' : 'Code sent.', 'success');
    }).catch(function (err) {
      var msg = apiError(err, 'Could not send a code.');
      setError(msg);
      if (toast) toast(msg, 'error');
    }).finally(function () { setBusy(false); });
  }

  function confirmPhone(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    api('POST', '/api/auth/account/phone/confirm', { phone_e164: phone, code: phoneCode }).then(function (res) {
      setPhoneCode('');
      setPhoneSent(false);
      if (res && res.user && res.user.phone_e164) setPhone(res.user.phone_e164);
      if (toast) toast('Phone number updated.', 'success');
    }).catch(function (err) {
      var msg = apiError(err, 'That code could not be confirmed.');
      setError(msg);
      if (toast) toast(msg, 'error');
    }).finally(function () { setBusy(false); });
  }

  function changePassword(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    api('POST', '/api/auth/account/password', { current_password: currentPassword, password: nextPassword }).then(function (res) {
      if (res && res.token && typeof window !== 'undefined') window.localStorage.setItem('authToken', res.token);
      setCurrentPassword('');
      setNextPassword('');
      if (toast) toast('Password updated. Other sessions were signed out.', 'success');
    }).catch(function (err) {
      var msg = apiError(err, 'Could not change the password.');
      setError(msg);
      if (toast) toast(msg, 'error');
    }).finally(function () { setBusy(false); });
  }

  return (
    <div className="acct-panel">
      <h3 className="acct-title">My account</h3>
      <p className="acct-help">Change the email, the mobile number used for sign-in codes, or the password. A new password signs out your other sessions.</p>
      {error ? <div className="acct-error">{error}</div> : null}
      <form className="acct-form" onSubmit={changeEmail}>
        <label className="acct-label">Email
          <input className="acct-input" type="email" value={email} onChange={function (e) { setEmail(e.target.value); }} required />
        </label>
        <label className="acct-label">Current password
          <input className="acct-input" type="password" value={emailPassword} onChange={function (e) { setEmailPassword(e.target.value); }} autoComplete="current-password" required />
        </label>
        <div><button className="acct-button" type="submit" disabled={busy}>Update email</button></div>
      </form>
      <form className="acct-form acct-section" onSubmit={phoneSent ? confirmPhone : sendPhoneCode}>
        <label className="acct-label">MFA phone
          <input className="acct-input" type="tel" value={phone} onChange={function (e) { setPhone(e.target.value); setPhoneSent(false); }} placeholder="+14155551234" required />
        </label>
        {phoneSent ? (
          <label className="acct-label">Code sent to the new number
            <input className="acct-input" value={phoneCode} onChange={function (e) { setPhoneCode(e.target.value); }} inputMode="numeric" autoComplete="one-time-code" required />
          </label>
        ) : null}
        <div className="acct-row">
          <button className="acct-button" type="submit" disabled={busy}>{phoneSent ? 'Confirm phone' : 'Send code'}</button>
          {phoneSent ? <button className="acct-button-ghost" type="button" onClick={function () { setPhoneSent(false); }}>Use a different number</button> : null}
        </div>
      </form>
      <form className="acct-form acct-section" onSubmit={changePassword}>
        <label className="acct-label">Current password
          <input className="acct-input" type="password" value={currentPassword} onChange={function (e) { setCurrentPassword(e.target.value); }} autoComplete="current-password" required />
        </label>
        <label className="acct-label">New password
          <input className="acct-input" type="password" value={nextPassword} onChange={function (e) { setNextPassword(e.target.value); }} autoComplete="new-password" required />
        </label>
        <p className="acct-muted">At least 10 characters, not your email, and not a common password.</p>
        <div><button className="acct-button" type="submit" disabled={busy}>Update password</button></div>
      </form>
    </div>
  );
}

export function ForgotPasswordForm({ api, onBack }) {
  var [email, setEmail] = useState('');
  var [message, setMessage] = useState('');
  var [error, setError] = useState('');
  var [busy, setBusy] = useState(false);

  function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    api('POST', '/api/auth/forgot-password', { email: email }).then(function (res) {
      setMessage((res && res.message) || 'If an account exists for that email, password reset instructions are on the way.');
    }).catch(function (err) {
      setError(apiError(err, 'Could not send reset instructions. Try again shortly.'));
    }).finally(function () { setBusy(false); });
  }

  return (
    <form className="acct-form" onSubmit={submit}>
      <h2 className="acct-title">Forgot password</h2>
      <p className="acct-help">Enter your email. If an account exists, reset instructions are sent when email delivery is configured. Otherwise a super admin can set a temporary password.</p>
      <label className="acct-label">Email
        <input className="acct-input" type="email" value={email} onChange={function (e) { setEmail(e.target.value); }} required autoComplete="username" />
      </label>
      {message ? <div className="acct-ok">{message}</div> : null}
      {error ? <div className="acct-error">{error}</div> : null}
      <div className="acct-row">
        <button className="acct-button" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send reset link'}</button>
        <button className="acct-button-ghost" type="button" onClick={onBack}>Back to sign in</button>
      </div>
    </form>
  );
}

export function ResetPasswordForm({ api, token, onDone }) {
  var [password, setPassword] = useState('');
  var [confirm, setConfirm] = useState('');
  var [error, setError] = useState('');
  var [message, setMessage] = useState('');
  var [busy, setBusy] = useState(false);

  function submit(e) {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Those passwords do not match.');
      return;
    }
    setBusy(true);
    api('POST', '/api/auth/reset-password', { token: token, password: password }).then(function (res) {
      setMessage((res && res.message) || 'Password updated. Sign in with your new password.');
    }).catch(function (err) {
      setError(apiError(err, 'That reset link could not be used.'));
    }).finally(function () { setBusy(false); });
  }

  return (
    <form className="acct-form" onSubmit={submit}>
      <h2 className="acct-title">Choose a new password</h2>
      <p className="acct-help">At least 10 characters, not your email, and not a common password.</p>
      <label className="acct-label">New password
        <input className="acct-input" type="password" value={password} onChange={function (e) { setPassword(e.target.value); }} autoComplete="new-password" required />
      </label>
      <label className="acct-label">Confirm password
        <input className="acct-input" type="password" value={confirm} onChange={function (e) { setConfirm(e.target.value); }} autoComplete="new-password" required />
      </label>
      {message ? <div className="acct-ok">{message}</div> : null}
      {error ? <div className="acct-error">{error}</div> : null}
      <div className="acct-row">
        <button className="acct-button" type="submit" disabled={busy || !!message}>{busy ? 'Saving…' : 'Update password'}</button>
        <button className="acct-button-ghost" type="button" onClick={onDone}>Back to sign in</button>
      </div>
    </form>
  );
}

export function ForceChangePassword({ user, api, toast, onChanged, onSignOut }) {
  var [currentPassword, setCurrentPassword] = useState('');
  var [password, setPassword] = useState('');
  var [confirm, setConfirm] = useState('');
  var [error, setError] = useState('');
  var [busy, setBusy] = useState(false);

  function submit(e) {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Those passwords do not match.');
      return;
    }
    setBusy(true);
    api('POST', '/api/auth/account/password', { current_password: currentPassword, password: password }).then(function (res) {
      if (res && res.token && typeof window !== 'undefined') window.localStorage.setItem('authToken', res.token);
      if (toast) toast('Password updated.', 'success');
      if (onChanged) onChanged(res && res.user, res && res.token);
    }).catch(function (err) {
      var msg = apiError(err, 'Could not change the password.');
      setError(msg);
      if (toast) toast(msg, 'error');
    }).finally(function () { setBusy(false); });
  }

  return (
    <main className="acct-gate">
      <form className="acct-card acct-form" onSubmit={submit}>
        <h1 className="acct-title">Change your password</h1>
        <p className="acct-help">
          {(user && user.email) ? user.email + ' must set a new password before continuing.' : 'Set a new password before continuing.'}
          {' '}Use at least 10 characters. It cannot match your email or be a common password.
        </p>
        <label className="acct-label">Current password
          <input className="acct-input" type="password" value={currentPassword} onChange={function (e) { setCurrentPassword(e.target.value); }} autoComplete="current-password" required />
        </label>
        <label className="acct-label">New password
          <input className="acct-input" type="password" value={password} onChange={function (e) { setPassword(e.target.value); }} autoComplete="new-password" required />
        </label>
        <label className="acct-label">Confirm new password
          <input className="acct-input" type="password" value={confirm} onChange={function (e) { setConfirm(e.target.value); }} autoComplete="new-password" required />
        </label>
        {error ? <div className="acct-error">{error}</div> : null}
        <div className="acct-row">
          <button className="acct-button" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save password'}</button>
          {onSignOut ? <button className="acct-button-ghost" type="button" onClick={onSignOut}>Sign out</button> : null}
        </div>
      </form>
    </main>
  );
}

export function ForcePhoneSetup({ user, api, toast, onDone, onSignOut }) {
  var [phone, setPhone] = useState('');
  var [code, setCode] = useState('');
  var [sent, setSent] = useState(false);
  var [error, setError] = useState('');
  var [busy, setBusy] = useState(false);

  function sendCode(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    api('POST', '/api/auth/account/phone/start', { phone_e164: phone }).then(function (res) {
      setSent(true);
      if (toast) toast(res && res.phone_hint ? 'Code sent to ' + res.phone_hint + '.' : 'Code sent.', 'success');
    }).catch(function (err) {
      setError(apiError(err, 'Could not send a code.'));
    }).finally(function () { setBusy(false); });
  }

  function confirm(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    api('POST', '/api/auth/account/phone/confirm', { phone_e164: phone, code: code }).then(function (res) {
      if (toast) toast('Phone verified. Sign-in codes will go to this number.', 'success');
      if (onDone) onDone(Object.assign({}, user, (res && res.user) || {}, { phone_setup_required: false }));
    }).catch(function (err) {
      setError(apiError(err, 'That code could not be confirmed.'));
    }).finally(function () { setBusy(false); });
  }

  return (
    <main className="acct-gate">
      <form className="acct-card acct-form" onSubmit={sent ? confirm : sendCode}>
        <h1 className="acct-title">Verify your mobile number</h1>
        <p className="acct-help">
          Your role signs in with a text-message code. Add the mobile number we should send it to.
        </p>
        <label className="acct-label">Mobile number
          <input className="acct-input" type="tel" value={phone} onChange={function (e) { setPhone(e.target.value); setSent(false); }} placeholder="+1 619 555 0100" autoComplete="tel" required />
        </label>
        {sent ? (
          <label className="acct-label">Code from the text
            <input className="acct-input" inputMode="numeric" value={code} onChange={function (e) { setCode(e.target.value); }} autoComplete="one-time-code" required />
          </label>
        ) : null}
        {error ? <div className="acct-error">{error}</div> : null}
        <div className="acct-row">
          <button className="acct-button" type="submit" disabled={busy}>{busy ? 'Working…' : (sent ? 'Verify' : 'Send code')}</button>
          {onSignOut ? <button className="acct-button-ghost" type="button" onClick={onSignOut}>Sign out</button> : null}
        </div>
      </form>
    </main>
  );
}
